import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { ordersApi } from "@/lib/api";
import CertificatePreview from "@/components/CertificatePreview";
import { Check, Download, Loader2, ArrowRight } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const Confirmation = () => {
  const { orderId } = useParams();
  const { t } = useLanguage();
  const [order, setOrder] = useState(null);
  const [generating, setGenerating] = useState(false);
  const certRef = useRef(null);

  useEffect(() => {
    ordersApi.get(orderId).then(setOrder).catch(() => setOrder(null));
  }, [orderId]);

  const downloadPdf = async () => {
    if (!certRef.current) return;
    setGenerating(true);
    try {
      const canvas = await html2canvas(certRef.current, {
        scale: 2,
        backgroundColor: "#FBF6E9",
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const ratio = canvas.height / canvas.width;
      const imgW = pageW - 60;
      const imgH = imgW * ratio;
      const y = Math.max(30, (pageH - imgH) / 2);
      pdf.addImage(imgData, "PNG", 30, y, imgW, imgH);
      pdf.save(`certificat-adoption-${order.toy_snapshot.name}.pdf`);
    } catch (e) {
      console.error(e);
    } finally {
      setGenerating(false);
    }
  };

  if (!order) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <p className="font-body text-muted-foreground">{t.common.loading}</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16 md:py-24" data-testid="confirmation-page">
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-moss/15 text-moss mb-6">
          <Check size={28} strokeWidth={1.5} />
        </div>
        <p className="font-script text-camel text-3xl">{t.confirmation.subtitle}</p>
        <h1 className="font-serif-display text-5xl md:text-6xl text-ink mt-1">
          {order.toy_snapshot.name}
        </h1>
        <p className="font-body text-sm text-muted-foreground mt-6 leading-relaxed">
          {t.confirmation.shippingInfo}
        </p>

        <div className="mt-8 inline-flex flex-wrap items-center justify-center gap-6 text-xs uppercase tracking-widest text-muted-foreground">
          <div>
            <div>{t.confirmation.orderNumber}</div>
            <div className="text-ink font-body normal-case tracking-normal mt-1" data-testid="order-number">
              {order.order_number}
            </div>
          </div>
          <div className="hidden md:block w-px h-8 bg-border" />
          <div>
            <div>{t.confirmation.certificateNumber}</div>
            <div className="text-ink font-body normal-case tracking-normal mt-1" data-testid="certificate-number">
              {order.certificate_number}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-14">
        <CertificatePreview
          ref={certRef}
          toy={order.toy_snapshot}
          buyer={{
            firstName: order.buyer_first_name,
            lastName: order.buyer_last_name,
            isGift: order.is_gift,
            giftRecipient: order.gift_recipient,
          }}
          message={order.adoption_message}
          certificateNumber={order.certificate_number}
          dateOverride={order.created_at}
        />
      </div>

      <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
        <button
          onClick={downloadPdf}
          disabled={generating}
          data-testid="download-certificate"
          className="inline-flex items-center gap-3 bg-terracotta text-background px-7 py-3.5 rounded-full font-body text-sm tracking-wide hover:shadow-warm transition-all disabled:opacity-60"
        >
          {generating ? (
            <>
              <Loader2 size={16} className="animate-spin" /> {t.confirmation.generating}
            </>
          ) : (
            <>
              <Download size={16} strokeWidth={1.5} /> {t.confirmation.download}
            </>
          )}
        </button>
        <Link
          to="/catalogue"
          className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-ink/20 text-ink font-body text-sm hover:bg-cream transition-all"
        >
          {t.confirmation.continueShopping} <ArrowRight size={14} strokeWidth={1.5} />
        </Link>
      </div>
    </div>
  );
};

export default Confirmation;
